<template>
  <div
    ref="container"
    :class="b()"
  >
    <button
      :class="b('button', { active: showOverlay })"
      type="button"
      aria-haspopup="true"
      :aria-expanded="showOverlay"
      :aria-label="$t('c-language-selector.aria-label-button')"
      @click="onShowOverlay"
    >
      <span :class="b('button-text')">
        {{ currentLanguage.label }}
      </span>
      <span :class="b('button-icon')"></span>
    </button>
    <div
      :class="b('overlay', { show: showOverlay, overlayPosition })"
      :aria-hidden="!showOverlay"
      :aria-label="$t('c-language-selector.aria-label-overlay')"
      tabindex="1"
    >
      <ul :class="b('list')">
        <li
          v-for="locale in availableLocales"
          :key="locale.locale"
          :class="b('list-item')"
        >
          <a
            :class="b('list-item-link')"
            :href="locale.url"
          >
            {{ locale.label }}
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
  import { Ref, defineComponent, ref } from 'vue';
  import useSessionStore from '@/stores/session';
  import { Locale } from '@/types/locales';

  type Setup = {
    sessionStore: ReturnType<typeof useSessionStore>;
    container: Ref<HTMLDivElement | null | undefined>;
  };

  type Data = {
    showOverlay: boolean;
  };

  /**
   * A button to show the language selector overlay.
   */
  export default defineComponent({
    name: 'c-language-selector',

    // components: {},

    props: {
      /**
       * Forces the overlay to open on top.
       */
      overlayPosition: {
        type: String,
        default: 'auto',
        validator: (value: string) => ['auto', 'top'].includes(value),
      },
    },
    // emits: [],

    setup(): Setup {
      return {
        sessionStore: useSessionStore(),
        container: ref(),
      };
    },
    data(): Data {
      return {
        showOverlay: false,
      };
    },

    computed: {
      availableLocales(): Locale[] {
        return this.sessionStore.availableLocales || [];
      },

      currentLanguage(): Locale {
        const found = this.sessionStore.availableLocales.find((item) => item.locale === this.sessionStore.locale);

        if (found) {
          return found;
        }

        if (this.sessionStore.availableLocales.length) {
          return this.sessionStore.availableLocales[0] as Locale;
        }

        // eslint-disable-next-line no-console
        console.error('c-language-selector: no matching locale found.');

        return {} as Locale;
      },
    },
    watch: {
      showOverlay() {
        if (this.showOverlay) {
          document.addEventListener('click', this.handleOutsideClick);
        } else {
          document.removeEventListener('click', this.handleOutsideClick);
        }
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      document.removeEventListener('click', this.handleOutsideClick);
    },
    // unmounted() {},

    methods: {
      onShowOverlay() {
        this.showOverlay = !this.showOverlay;
      },

      /**
       * Hides the overlay if the user clicks somewhere else than inside the container.
       */
      handleOutsideClick(event: Event) {
        if (!this.container?.contains(event.target as Node)) {
          this.showOverlay = false;
        }
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .c-language-selector {
    $this: &;
    $c-header-breakpoint: md;

    position: relative;

    &__button {
      @include mixins.hover-effect--default;

      background: var(--theme-color-grayscale--200);
      color: var(--theme-color-grayscale--950);
      padding: 0.66rem 0.75rem;
      min-width: 11rem;
      border-radius: 1.5rem;
      border: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      cursor: pointer;

      &--active {
        #{$this}__button-icon {
          transform: rotate(180deg);
        }
      }
    }

    &__button-icon {
      --button-icon-size: 1rem;

      display: block;
      width: var(--button-icon-size);
      height: var(--button-icon-size);
      background-color: var(--theme-color-grayscale--1000);
      mask-image: url('@/assets/icons/i-chevron--down.svg');
      mask-size: var(--button-icon-size);
      mask-repeat: no-repeat;
      mask-position: center;
    }

    &__overlay {
      $overlay-offset: 3rem;

      display: none;
      position: absolute;
      background-color: var(--theme-color-grayscale--900);
      box-shadow: 0 5px 15px var(--theme-color-grayscale--200);
      max-width: fit-content;
      min-width: 11rem;
      margin: auto;

      &--show {
        display: flex;
      }

      &--overlay-position-auto {
        right: 0;
        left: 0;
        bottom: $overlay-offset;
        border-bottom: 2px solid var(--theme-color-grayscale--100);

        @include mixins.media($c-header-breakpoint) {
          right: 0;
          left: auto;
          bottom: unset;
          top: $overlay-offset;
          min-width: 24rem;
          border-top: 2px solid var(--theme-color-grayscale--100);
          border-bottom: 0;
        }
      }

      &--overlay-position-top {
        right: 0;
        left: 0;
        bottom: $overlay-offset;
        border-bottom: 2px solid var(--theme-color-grayscale--100);

        @include mixins.media($c-header-breakpoint) {
          min-width: 24rem;
        }
      }
    }

    &__list {
      display: flex;
      flex-direction: column;
      width: 100%;

      @include mixins.media($c-header-breakpoint) {
        display: grid;
        grid-template-columns: 1fr 1fr;
        column-gap: 1.5rem;
        padding: 0.75rem;
      }
    }

    &__list-item {
      border-bottom: 1px solid var(--theme-color-grayscale--800);

      &:last-child,
      &:nth-last-child(2):nth-child(odd) {
        border-bottom: none;
      }
    }

    &__list-item-link {
      display: flex;
      padding: 0.66rem 0.75rem;
      font-size: variables.$font-size--small;
      text-decoration: none;

      &:hover {
        background: var(--theme-color-grayscale--800);
        color: var(--theme-color-grayscale--400);
      }
    }
  }
</style>
